/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import PulseLoader from 'react-spinners/PulseLoader';

// MaterialUI
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';

// app modules: components
import Price from 'components/Price';

// app: assets
import CssSubscriptionPlanCard from './SubscriptionPlanCard.scss';
import useOsPrefersDark from '../../hooks/useOsPrefersDark';

function SubscriptionPlanCard(props) {
  const {
    chip,
    className,
    disabled,
    loading,
    onClick,
    priceInfo,
    selected,
    showPeriod,
    showCancelText,
    theme,
    title,
    unselected,
    withZeroPriceTrials,
    hasDoneGGTrial,
    platformId,
    additonalText,
  } = props;
  useStyles(CssSubscriptionPlanCard);
  const osPrefersDark = useOsPrefersDark();
  const [selectedTheme, setSelectedTheme] = useState(theme);

  const classes = classNames(
    'subscription-plan-card',
    `subscription-plan-card--theme-${theme}`,
    {
      'subscription-plan-card--selected': selected,
      'subscription-plan-card--unselected': unselected,
      'subscription-plan-card--disabled': disabled,
    },
    className,
  );

  useEffect(
    () => {
      if (osPrefersDark && theme === 'system') {
        setSelectedTheme('dark');
      }
    },
    [osPrefersDark, theme],
  );

  const {
    currency,
    interval,
    list_price,
    monthly_equivalent_price,
    offer_price,
    period,
    trial_days,
  } = useMemo(
    () =>
      priceInfo || {
        currency: null,
        interval: null,
        list_price: null,
        monthly_equivalent_price: null,
        offer_price: null,
        period: null,
        trial_days: null,
      },
    [priceInfo],
  );

  const trialDaysText = useMemo(
    () => {
      if (platformId === 'android') {
        return '14 days';
      }

      let text = `${trial_days} ${trial_days > 1 ? 'days' : 'day'}`;
      if (trial_days > 31) {
        const months = Math.round(trial_days / 30);
        if (months === 1) {
          text = 'month';
        } else {
          text = `${months} months`;
        }

        if (period === 'year' && trial_days % 365 === 0) {
          const years = Math.round(trial_days / 365);
          if (years === 1) {
            text = 'year';
          } else {
            text = `${years} years`;
          }
        }
      }
      return text;
    },
    [trial_days, period, platformId],
  );

  const canDoTrial = useMemo(
    () => {
      if (platformId === 'android') {
        return !hasDoneGGTrial;
      }

      return trial_days > 0;
    },
    [hasDoneGGTrial, platformId, trial_days],
  );

  if (loading) {
    return (
      <Card variant="outlined" className={classes}>
        <CardContent align="center">
          <Typography gutterBottom>{title}</Typography>
          <PulseLoader
            loading
            color={selectedTheme === 'dark' ? '#fff' : '#000'}
            sizeUnit="px"
            size={10}
          />
        </CardContent>
      </Card>
    );
  }

  const cancelText = showCancelText ? <p>Cancel any time</p> : null;
  const monthlyEquivalentPrice =
    (interval > 1 || period !== 'month') && monthly_equivalent_price && currency ? (
      <p>
        (<Price currency={currency} amount={monthly_equivalent_price} />/month )
      </p>
    ) : null;
  let footerText;
  if (canDoTrial && withZeroPriceTrials) {
    footerText = (
      <React.Fragment>
        <p>for the first {trialDaysText}</p>
        <p>
          Then just{' '}
          <Price reverse currency={currency} amount={offer_price} originalPrice={list_price} />
        </p>{' '}
        {monthlyEquivalentPrice}
      </React.Fragment>
    );
  } else {
    footerText = (
      <React.Fragment>
        {canDoTrial ? <p>{trialDaysText} free trial</p> : null}
        {monthlyEquivalentPrice || cancelText}
        {additonalText}
      </React.Fragment>
    );
  }
  return (
    <Card variant="outlined" className={classes} onClick={onClick}>
      {chip ? <div className="subscription-plan-card__chip">{chip}</div> : null}
      <CardActionArea disabled={disabled} className="subscription-plan-card__action-area">
        <CardContent className="subscription-plan-card__content" align="center">
          <Typography gutterBottom>{title} </Typography>
          <Typography variant="h2">
            {canDoTrial && withZeroPriceTrials ? (
              <Price
                currency={currency}
                amount={0}
                originalPrice={0}
                originalPriceClassName="subscription-plan-card__list-price"
              />
            ) : (
              <Price
                currency={currency}
                amount={offer_price}
                originalPrice={list_price}
                originalPriceClassName="subscription-plan-card__list-price"
              />
            )}
            {showPeriod ? <span className="subscription-plan-card__period">/{period}</span> : null}
          </Typography>
          {footerText}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

SubscriptionPlanCard.propTypes = {
  chip: PropTypes.node,
  className: PropTypes.string,
  platformId: PropTypes.string,
  additonalText: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  priceInfo: PropTypes.shape({
    currency: PropTypes.string,
    interval: PropTypes.number,
    list_price: PropTypes.string,
    monthly_equivalent_price: PropTypes.string,
    offer_price: PropTypes.string,
    period: PropTypes.string,
    trial_days: PropTypes.number,
  }),
  selected: PropTypes.bool,
  showCancelText: PropTypes.bool,
  showPeriod: PropTypes.bool,
  unselected: PropTypes.bool,
  theme: PropTypes.oneOf(['dark', 'light', 'system']),
  title: PropTypes.string.isRequired,
  withZeroPriceTrials: PropTypes.bool,
  hasDoneGGTrial: PropTypes.bool,
};

SubscriptionPlanCard.defaultProps = {
  chip: null,
  className: null,
  platformId: null,
  additonalText: null,
  disabled: false,
  hasDoneGGTrial: false,
  loading: false,
  onClick: null,
  priceInfo: null,
  selected: false,
  showCancelText: true,
  showPeriod: false,
  theme: 'light',
  unselected: false,
  withZeroPriceTrials: false,
};

export default SubscriptionPlanCard;
